import { useRef, useState } from "react";
import { Camera, Images, FolderOpen, UploadCloud } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { CameraCapture } from "@/components/CameraCapture";
import { canUseInAppCamera } from "@/lib/imagePrep";

/**
 * "Jak chcesz dodac paragon?" - three explicit sources for a receipt image.
 *
 *  1. Camera   -> in-app camera (getUserMedia, rear camera) rendered inside the
 *                 page. Android kills background browser/TWA processes while the
 *                 native camera app is open ("Not enough memory to complete the
 *                 previous operation"), which lost the photo; staying in-page
 *                 avoids that. If getUserMedia is unavailable or denied, the
 *                 native <input capture="environment"> is offered as fallback.
 *  2. Gallery  -> <input type="file" accept="image/*">  (device photo picker)
 *  3. File     -> <input type="file" accept="image/png,image/jpeg,image/webp"> (regular file picker)
 *
 * Whether a photo taken via the camera option is kept in the device gallery is
 * decided by the OS/browser, not by this app - we make no claim about it.
 *
 * `onFile(file)` receives the chosen File exactly as the previous single input
 * did, so the existing scan/OCR pipeline is unchanged.
 */
export function ReceiptSourceChooser({ onFile, dragOver = false, onDragOver, onDragLeave, onDrop }) {
  const { t } = useI18n();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const fileRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const inAppCamera = canUseInAppCamera();

  const pick = (ref) => (e) => {
    e.stopPropagation();
    const el = ref.current;
    if (!el) return;
    el.value = ""; // allow re-selecting the same photo
    el.click();
  };

  const onChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onFile(file);
  };

  const openCamera = (e) => {
    e.stopPropagation();
    if (inAppCamera) setCameraOpen(true);
    else pick(cameraRef)(e);
  };

  const useNativeCamera = () => {
    setCameraOpen(false);
    const el = cameraRef.current;
    if (el) { el.value = ""; el.click(); }
  };

  const options = [
    {
      key: "camera",
      ref: cameraRef,
      icon: Camera,
      label: t("scan.optCamera"),
      hint: inAppCamera ? t("scan.optCameraHint") : t("scan.optCameraHintNative"),
      primary: true,
      testId: "receipt-source-camera",
      onClick: openCamera,
    },
    {
      key: "gallery",
      ref: galleryRef,
      icon: Images,
      label: t("scan.optGallery"),
      hint: t("scan.optGalleryHint"),
      testId: "receipt-source-gallery",
    },
    {
      key: "file",
      ref: fileRef,
      icon: FolderOpen,
      label: t("scan.optFile"),
      hint: t("scan.optFileHint"),
      testId: "receipt-source-file",
    },
  ];

  return (
    <div
      data-testid="receipt-source-chooser"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`rounded-2xl border p-5 md:p-8 transition-colors duration-150 ${
        dragOver ? "border-cyan-300/50 bg-cyan-400/5 glow-cyan" : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className="text-center mb-5 md:mb-6">
        <div className="h-12 w-12 rounded-2xl mx-auto bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center mb-3">
          <UploadCloud className="text-cyan-300" style={{ width: 22, height: 22 }} />
        </div>
        <h2 className="font-display font-semibold text-lg md:text-xl" data-testid="receipt-source-title">{t("scan.chooserTitle")}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t("scan.chooserHelp")}</p>
      </div>

      <div className="grid gap-3">
        {options.map(({ key, ref, icon: Icon, label, hint, primary, testId, onClick }) => (
          <button
            key={key}
            type="button"
            data-testid={testId}
            onClick={onClick || pick(ref)}
            className={`group w-full text-left rounded-xl border px-4 py-4 md:py-4 flex items-center gap-4 min-h-[64px] transition-[transform,background-color,border-color] duration-150 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 ${
              primary
                ? "border-cyan-400/40 bg-cyan-400/15 hover:bg-cyan-400/20 glow-cyan"
                : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20"
            }`}
          >
            <span
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border ${
                primary ? "bg-cyan-400 text-slate-900 border-cyan-300" : "bg-cyan-400/10 text-cyan-300 border-cyan-400/25"
              }`}
            >
              <Icon style={{ width: 20, height: 20 }} />
            </span>
            <span className="min-w-0 flex-1">
              <span className={`block font-display font-semibold ${primary ? "text-cyan-50 text-base md:text-lg" : "text-sm md:text-base"}`}>{label}</span>
              <span className="block text-xs text-muted-foreground mt-0.5">{hint}</span>
            </span>
          </button>
        ))}
      </div>

      <p className="hidden md:block text-center text-xs text-muted-foreground mt-5">{t("scan.dragHint")}</p>

      {/* Dedicated inputs: `capture` on the camera input is what opens the rear camera on mobile. */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="receipt-camera-input" onChange={onChange} />
      <CameraCapture
        open={cameraOpen}
        onCancel={() => setCameraOpen(false)}
        onUseNativeCamera={useNativeCamera}
        onCapture={(file) => { setCameraOpen(false); onFile(file); }}
      />
      <input ref={galleryRef} type="file" accept="image/*" className="hidden" data-testid="receipt-gallery-input" onChange={onChange} />
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="receipt-file-input" onChange={onChange} />
    </div>
  );
}
